import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "kann_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, color = "White", quantity = 1, installType = "", installDesc = "") => {
    setItems((prev) => {
      const key = `${product.id}-${color}${installType ? "-" + installType : ""}`;
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          price: product.price,
          compare_at_price: product.compare_at_price || 0,
          image: product.images?.[0] || "",
          color,
          install_type: installType,
          install_desc: installDesc,
          quantity,
        },
      ];
    });
    setOpen(true);
  };

  const addRawItem = (line) => {
    const key = line.key || `${line.product_id || "custom"}-${Date.now()}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + (line.quantity || 1) } : i));
      return [...prev, { compare_at_price: 0, color: "", install_type: "", install_desc: "", image: "", quantity: 1, ...line, key }];
    });
    setOpen(true);
  };

  const updateQuantity = (key, quantity) => {
    if (quantity <= 0) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity } : i)));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const savings = useMemo(
    () => items.reduce((s, i) => s + ((i.compare_at_price || 0) > i.price ? (i.compare_at_price - i.price) * i.quantity : 0), 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{ items, addItem, addRawItem, updateQuantity, removeItem, clearCart, count, subtotal, savings, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
